// External libraries
import React, { useMemo } from 'react';
import { useStoreState } from '@/store';
import {
  FloatingLabelInput,
  FloatingLabelSelect,
} from '@checkout/react-components';
import { number, object } from 'prop-types';

// Internal Modules
import { getCountriesList, getTaxPayerIdMaxLength } from '../utils';
import { MarginSeparator, MinusLinkButton } from '../StyledComponents';
import { features, getContent, getLocale } from '@/lib';
import { logger } from '@/lib/logger';
import {
  AdditionalTaxResidencyTitle,
  Container,
  NoTaxIdReasonStyleOverrides,
  TINFieldInputStyleOverrides,
  TINFieldTooltipWrapper,
} from './StyledComponents';
import { CRS_TAX_DETAILS_FIELDS, CRS_TAXPAYERID_OPTIONS } from './../constants';
import { MinusIcon } from '@/components/Common/Icons';
import {
  crsTaxDetailsChangeHandler,
  removeTaxResidencyChangeHandler,
  shouldShowTINField,
} from './handlers.js';
import { getCrsNoTaxpayerIdReasonsSelectOptions } from './../utils';
import { Checkbox, ParentContainer } from '../../Common';
import { TINTooltip } from './TINTooltip.jsx';

const InlineCRSTaxDetails = ({ crsTaxDetails, index }) => {
  const {
    COUNTRY_CODE,
    TAX_PAYER_ID_TYPE,
    TAX_PAYER_ID_VALUE,
    NO_TAX_PAYER_ID_REASON,
  } = CRS_TAX_DETAILS_FIELDS;

  const { locale, country: onboardingCountry } = getLocale();

  const countriesList = useStoreState(({ api }) => {
    const crsTaxResidencyCountries = api.crsTaxResidencyCountries;
    const crsCountries = crsTaxResidencyCountries?.territories?.[locale] || [];
    if (crsCountries.length > 0) {
      return crsCountries;
    }

    return api.griffinMetadata?.territories?.[locale] || [];
  });
  const theme = features.get('rebrandFormFieldsTheme');

  const { countryCode, taxPayerIdType, taxPayerIdValue, noTaxPayerIdReason } =
    crsTaxDetails[index];

  const countryList = useMemo(
    () =>
      getCountriesList({
        countriesList,
        crsTaxDetails,
        currentTaxResidencyCountry: countryCode.value,
        onboardingCountry,
      }),
    [countriesList, crsTaxDetails, countryCode.value, onboardingCountry],
  );

  const doesUserHaveTIN =
    taxPayerIdType !== CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID;
  /// Content labels
  const taxIdValueLabel = getContent('crs.fieldLabels.taxIdentificationNumber');
  const countryLabel = getContent('crs.fieldLabels.country');
  const noTaxPayerIdLabel = getContent('crs.checkbox.noTaxPayerID');
  const provideReasonLabel = getContent('crs.fieldLabels.provideReason');
  const removeCountryLabel = getContent('crs.removeCountry');
  const additionalTaxResidencyLabel = getContent('crs.additionalTaxResidency');
  const shouldDisplayRemoveLink = index !== 0;

  const changeHandler = (newValue, attributeChange, clickEvent) => {
    crsTaxDetailsChangeHandler(newValue, attributeChange, index, crsTaxDetails);
    if (clickEvent) {
      logger.fpti(`crs_${clickEvent.target.id}`, {
        selectedOption: newValue,
        transition_name: `process_${clickEvent.target.id}_${clickEvent.type}`,
      });
      logger.info(`crs_${clickEvent.target.id}_${clickEvent.type}`);
    }
  };

  return (
    <Container data-testid={`crs-tax-details-${index}`} index={index}>
      {index > 0 && (
        <AdditionalTaxResidencyTitle>
          {additionalTaxResidencyLabel}
        </AdditionalTaxResidencyTitle>
      )}

      <FloatingLabelSelect
        changeHandler={(clickEvent, newValue) => {
          changeHandler(newValue, COUNTRY_CODE, clickEvent);
        }}
        displayError={countryCode.displayError}
        emptyOption={{ enabled: true }}
        error={countryCode.error}
        id={`${CRS_TAX_DETAILS_FIELDS.COUNTRY_CODE}_${index}`}
        label={countryLabel}
        selectOptions={countryList}
        theme={theme}
        value={countryCode.value}
      />

      <MarginSeparator />
      {shouldShowTINField(onboardingCountry, countryCode.value, index) && (
        <>
          {doesUserHaveTIN && (
            <TINFieldTooltipWrapper>
              <FloatingLabelInput
                changeHandler={(_, newValue) => {
                  changeHandler(newValue, TAX_PAYER_ID_VALUE);
                }}
                displayError={taxPayerIdValue.displayError}
                inputStyleOverrides={TINFieldInputStyleOverrides}
                error={taxPayerIdValue.error}
                id={`${TAX_PAYER_ID_VALUE}_${index}`}
                label={taxIdValueLabel}
                maxLength={getTaxPayerIdMaxLength({
                  countryCode,
                  taxPayerIdValue,
                })}
                value={taxPayerIdValue.value}
              />
              <TINTooltip />
            </TINFieldTooltipWrapper>
          )}
          <ParentContainer margin="20px 0 20px">
            <Checkbox
              changeHandler={event => {
                const newValue = event.target.checked
                  ? CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID
                  : CRS_TAXPAYERID_OPTIONS.TIN;
                changeHandler(newValue, TAX_PAYER_ID_TYPE);
              }}
              checked={taxPayerIdType === CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID}
              id={`${TAX_PAYER_ID_TYPE}_${index}`}
              label={noTaxPayerIdLabel}
              required={false}
            />
          </ParentContainer>
          {!doesUserHaveTIN && (
            <>
              <FloatingLabelSelect
                changeHandler={(clickEvent, newValue) => {
                  changeHandler(newValue, NO_TAX_PAYER_ID_REASON, clickEvent);
                }}
                displayError={noTaxPayerIdReason.displayError}
                emptyOption={{ enabled: true }}
                error={noTaxPayerIdReason.error}
                id={`${CRS_TAX_DETAILS_FIELDS.NO_TAX_PAYER_ID_REASON}_${index}`}
                label={provideReasonLabel}
                selectOptions={getCrsNoTaxpayerIdReasonsSelectOptions()}
                selectStyleOverrides={NoTaxIdReasonStyleOverrides}
                theme={theme}
                value={noTaxPayerIdReason.value}
              />
              <MarginSeparator />
            </>
          )}
        </>
      )}
      {shouldDisplayRemoveLink && (
        <MinusLinkButton
          type="button"
          data-testid={`crs-remove-tax-country-${index}`}
          onClick={() => removeTaxResidencyChangeHandler(index, crsTaxDetails)}
        >
          <MinusIcon height={30} width={30} />
          {removeCountryLabel}
        </MinusLinkButton>
      )}
    </Container>
  );
};

InlineCRSTaxDetails.propTypes = {
  crsTaxDetails: object,
  index: number,
};

export { InlineCRSTaxDetails };
